import { useEffect, useState } from 'react';

import styles from './DeleteButton.module.css';

export interface DeleteButtonProps {
  /** What is being deleted, for the accessible name. */
  label: string;
  onDelete: () => void;
  disabled?: boolean;
  busy?: boolean;
  /** `compact` is the badge that sits on a card thumbnail. */
  size?: 'compact' | 'regular';
}

/**
 * Delete, behind one click of confirmation.
 *
 * Click-to-confirm rather than a dialog: a harness deletes a file to see what
 * that does to the pipeline, so the action wants to be quick — but a delete on
 * a card grid sits under a moving cursor, and a single click is one slip away
 * from destroying the wrong document.
 *
 * The armed state disarms itself. A control left saying "Confirm" indefinitely
 * is a trap for the next click, which by then means something the reader has
 * forgotten they asked for.
 */
const DISARM_AFTER_MS = 4000;

export function DeleteButton({
  label,
  onDelete,
  disabled = false,
  busy = false,
  size = 'regular',
}: DeleteButtonProps) {
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    if (!armed) {
      return;
    }
    const timer = setTimeout(() => setArmed(false), DISARM_AFTER_MS);
    return () => clearTimeout(timer);
  }, [armed]);

  const onClick = () => {
    if (armed) {
      setArmed(false);
      onDelete();
      return;
    }
    setArmed(true);
  };

  return (
    <button
      aria-label={armed ? `Confirm delete ${label}` : `Delete ${label}`}
      aria-busy={busy}
      className={`${styles.button} ${styles[size]} ${armed ? styles.armed : ''}`}
      disabled={disabled || busy}
      onClick={(event) => {
        // The card behind this is itself a button that opens the document.
        event.stopPropagation();
        onClick();
      }}
      type="button"
    >
      {armed ? (
        <span className={styles.confirm}>Confirm</span>
      ) : (
        <svg aria-hidden="true" className={styles.icon} viewBox="0 0 16 16">
          <path
            d="M6 2h4M2.5 4h11M4.5 4l.6 9a1 1 0 0 0 1 1h3.8a1 1 0 0 0 1-1l.6-9M6.5 7v4M9.5 7v4"
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeWidth="1.3"
          />
        </svg>
      )}
    </button>
  );
}
